import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { alpha, useTheme, Box } from '@mui/material';
import { forwardRef } from 'react';

interface StyledLoadingButtonProps extends LoadingButtonProps {
  children: React.ReactNode;
  gradient?: boolean;
  shimmer?: boolean;
}

const StyledLoadingButton = forwardRef<HTMLButtonElement, StyledLoadingButtonProps>(
  ({ children, loading, disabled, gradient = true, shimmer = true, sx, ...props }, ref) => {
    const theme = useTheme();

    const getBackground = () => {
      if (loading || disabled) {
        return alpha(theme.palette.primary.main, 0.8);
      }
      if (gradient) {
        return `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(
          theme.palette.primary.dark,
          0.9
        )})`;
      }
      return theme.palette.primary.main;
    };

    const getHoverBackground = () => {
      if (loading || disabled) {
        return alpha(theme.palette.primary.main, 0.8);
      }
      if (gradient) {
        return `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`;
      }
      return theme.palette.primary.dark;
    };

    return (
      <LoadingButton
        ref={ref}
        size="large"
        variant="contained"
        fullWidth
        loading={loading}
        disabled={disabled}
        loadingPosition="start"
        sx={{
          py: 1.5,
          borderRadius: 1.5,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          position: 'relative',
          background: getBackground(),
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: getHoverBackground(),
            transform: loading || disabled ? 'none' : 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '& .MuiLoadingButton-loadingIndicator': {
            color: 'white',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          },
          '&.MuiLoadingButton-loading': {
            color: 'transparent',
            ...(shimmer && {
              '&::after': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                background: `linear-gradient(90deg, 
                  transparent, 
                  ${alpha(theme.palette.common.white, 0.1)}, 
                  transparent)`,
                animation: 'shimmer 2s infinite',
              },
            }),
          },
          '@keyframes shimmer': {
            '0%': {
              transform: 'translateX(-100%)',
            },
            '100%': {
              transform: 'translateX(100%)',
            },
          },
          '&:disabled': {
            background: alpha(theme.palette.primary.main, 0.6),
            color: alpha(theme.palette.common.white, 0.8),
          },
          ...sx,
        }}
        {...props}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            opacity: loading || disabled ? 0.8 : 1,
          }}
        >
          {children}
        </Box>
      </LoadingButton>
    );
  }
);

StyledLoadingButton.displayName = 'StyledLoadingButton';

export default StyledLoadingButton;
